import React, { memo, useCallback } from "react";
import { makeStyles } from "@material-ui/core";
import {
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  SortDirection,
  Table,
} from "react-virtualized";
import clsx from "clsx";
import Downwards from "../../icons/Downwards";
import Upwards from "../../icons/Upwards";
import { View } from "../../Base";

const useStyles = makeStyles(({ typography }) => ({
  autoSizerWrapper: {
    flex: "1 1 auto",
    overflow: "auto hidden",
    scrollbarWidth: "thin",
  },
  row: {
    display: "flex",
    borderBottom: "1px solid #eee",
    paddingRight: "0 !important",
  },
}));

const WithTestId = (Component) => {
  return function WithTestIdComponent({ testId, ...props }) {
    return (
      <div data-testid={testId}>
        <Component {...props} />
      </div>
    );
  };
};

const TableRowWithTestId = WithTestId(defaultTableRowRenderer);

const VirtualizedTable = ({
  disableHeader,
  headerHeight,
  rowHeight,
  overscanRowCount,
  getRowData,
  columns,
  data,
  tableWidth,
  handleRowClick,
  classes,
  order,
  isAscending,
  setOrder,
  withFooter,
}) => {
  const styles = useStyles();

  const rowGetter = useCallback(
    ({ index }) => {
      const isFooter = data.length - 1 < index;
      if (getRowData) {
        return getRowData(index);
      }
      return isFooter ? null : data[index];
    },
    [data, getRowData]
  );

  const cellDataGetter = useCallback(({ dataKey, rowData }) => {
    return rowData && rowData[dataKey];
  }, []);

  const columnHeaderRenderer = useCallback(
    ({ sortBy, sortDirection, columnData }) => {
      const { sortByAccessor, accessor, Header } = columnData;
      const orderAccessor = sortByAccessor || accessor;
      const setOwnOrder = () =>
        orderAccessor ? setOrder(orderAccessor) : null;

      return (
        <View className={classes.headerCellContainer} onClick={setOwnOrder}>
          {!!Header && <Header data={data} />}
          <span className={classes.sortArrow}>
            {sortBy === orderAccessor &&
              (sortDirection === SortDirection.ASC ? (
                <Downwards />
              ) : (
                <Upwards />
              ))}
          </span>
        </View>
      );
    },
    [classes.headerCellContainer, classes.sortArrow, setOrder, data]
  );

  const columnCellRenderer = useCallback(
    ({ cellData, columnData, dataKey, rowData, rowIndex }) => {
      const { Cell, accessor, columnConfig, Footer } = columnData;
      const { width } = columnConfig;
      const isFooter = data.length - 1 < rowIndex;
      const CellComponent = isFooter ? Footer : Cell;
      return (
        <View
          key={`${dataKey}_td_${accessor}_${rowIndex}`}
          data-testId={`td_${accessor}`}
          className={classes.cellClassName}
          style={{ maxWidth: width, minWidth: width }}
        >
          {CellComponent && (
            <CellComponent rowData={rowData} value={cellData} />
          )}
        </View>
      );
    },
    [classes.cellClassName, data.length]
  );

  const rowRenderer = useCallback(
    (props) => {
      const { rowData, className, index } = props;
      const rowProps =
        rowData && rowData.selected
          ? { ...props, className: clsx(className, classes.selectedRow) }
          : props;
      const testId =
        data.length - 1 < index ? "tr_footer" : `tr_${rowData?.key || index}`;
      return <TableRowWithTestId {...rowProps} testId={testId} />;
    },
    [classes.selectedRow, data.length]
  );

  const headerRowRenderer = useCallback(
    ({ className, columns, style }) => {
      return (
        <div
          className={clsx(className, classes.headerCell)}
          role="row"
          style={style}
        >
          {columns}
        </div>
      );
    },
    [classes.headerCell]
  );

  return (
    <View className={classes.tableWrap}>
      <div className={styles.autoSizerWrapper}>
        <AutoSizer>
          {({ width, height }) => (
            <Table
              ref="Table"
              disableHeader={disableHeader}
              headerClassName={classes.headerCellContainer}
              gridClassName={classes.gridContainer}
              headerHeight={headerHeight}
              height={height}
              onRowClick={handleRowClick}
              overscanRowCount={overscanRowCount}
              rowHeight={rowHeight}
              rowGetter={rowGetter}
              rowCount={data.length + !!withFooter}
              rowClassName={styles.row}
              sortBy={order}
              sortDirection={
                isAscending ? SortDirection.ASC : SortDirection.DESC
              }
              headerRowRenderer={headerRowRenderer}
              rowRenderer={rowRenderer}
              width={tableWidth > width ? tableWidth : width}
              scrollToAlignment="end"
            >
              {columns.map((column) => {
                if (!column.columnConfig) {
                  return null;
                }
                return (
                  <Column
                    dataKey={column.accessor}
                    headerRenderer={columnHeaderRenderer}
                    cellRenderer={columnCellRenderer}
                    width={column.columnConfig.width}
                    cellDataGetter={cellDataGetter}
                    columnData={column}
                  />
                );
              })}
            </Table>
          )}
        </AutoSizer>
      </div>
    </View>
  );
};

export default memo(VirtualizedTable);
